import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { IIssue } from '@d-orca/shared/trusstor';
import { IssueCommentType } from '@d-orca/shared/trusstor';
import { addImageCommentApi } from '@apis/monitor';
import { useAuth } from '@contexts/useAuth';
import { OWNS_ERROR_FEEDBACK } from '@d-orca/shared/client/hooks';

type CacheShape = { issues: IIssue[] } | undefined;

interface AddImageCommentVariables {
  siteId: string;
  issueId: string;
  file: File;
}

interface AddImageCommentContext {
  previous: CacheShape;
  queryKey: string[];
  tempBlobUrl: string;
}

export function useAddImageComment() {
  const qc = useQueryClient();
  const { user } = useAuth();

  return useMutation<IIssue, Error, AddImageCommentVariables, AddImageCommentContext>({
    // Its one caller, the Hub composer, names the refusal inline.
    meta: OWNS_ERROR_FEEDBACK,
    mutationFn: ({ siteId, issueId, file }) => addImageCommentApi(siteId, issueId, file),

    onMutate: async ({ siteId, issueId, file }) => {
      const queryKey = ['monitor', 'issues', siteId];
      await qc.cancelQueries({ queryKey });

      const previous = qc.getQueryData<CacheShape>(queryKey);
      const tempBlobUrl = URL.createObjectURL(file);

      const tempComment = {
        commentId: `temp-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        dateCreated: new Date(),
        sender: { name: user?.username ?? '?', username: user?.username ?? '?' },
        content: {
          type: IssueCommentType.IMAGE,
          value: tempBlobUrl,
          sizeInBytes: file.size,
          filename: file.name,
        },
      };

      qc.setQueryData<CacheShape>(queryKey, (current) => {
        if (!current) return current;
        return {
          issues: current.issues.map((i) => {
            if (i._id !== issueId) return i;
            return {
              ...i,
              comments: [...(i.comments ?? []), tempComment as never],
            } as IIssue;
          }),
        };
      });

      return { previous, queryKey, tempBlobUrl };
    },

    onSuccess: (updatedIssue, _vars, context) => {
      qc.setQueryData<CacheShape>(context!.queryKey, (current) => {
        if (!current) return current;
        return {
          issues: current.issues.map((i) =>
            i._id === updatedIssue._id ? updatedIssue : i,
          ),
        };
      });
      URL.revokeObjectURL(context!.tempBlobUrl);
    },

    onError: (_err, _vars, context) => {
      if (context?.previous) {
        qc.setQueryData(context.queryKey, context.previous);
      }
      if (context) URL.revokeObjectURL(context.tempBlobUrl);
    },
  });
}
